import { Injectable } from '@angular/core';

export type StateValidator<T> = (obj: unknown) => obj is T;

@Injectable({
  providedIn: 'root',
})
export class FilterStateService {
  private readonly PREFIX = 'filter_state_';

  save<T>(key: string, state: T): void {
    try {
      localStorage.setItem(this.PREFIX + key, JSON.stringify(state));
    } catch {
      // Fail silently - saving filters is not critical
    }
  }

  restore<T>(key: string, validator?: StateValidator<T>): T | null {
    try {
      const serialized = localStorage.getItem(this.PREFIX + key);
      if (!serialized) {
        return null;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(serialized);
      } catch {
        this.clear(key);
        return null;
      }
      if (!validator) {
        return parsed as T;
      }
      if (!validator(parsed)) {
        this.clear(key);
        return null;
      }
      return parsed;
    } catch {
      this.clearSilently(key);
      return null;
    }
  }

  clear(key: string): void {
    try {
      localStorage.removeItem(this.PREFIX + key);
    } catch {
      // Fail silently
    }
  }

  private clearSilently(key: string): void {
    try {
      localStorage.removeItem(this.PREFIX + key);
    } catch {
      // Intentionally silent
    }
  }

  has(key: string): boolean {
    try {
      return localStorage.getItem(this.PREFIX + key) !== null;
    } catch {
      return false;
    }
  }
}
